import { type QueryClient, keepPreviousData, skipToken, useQuery } from '@tanstack/react-query';
import { getAlbumAssets, getFolderAssets } from '@/services/asset.service';
import { useAuthenticatedQueryFn } from '@/hooks/useAuthenticatedQuery';
import { type GetAssetsRoot } from '@/types/asset';
import { type SortState } from '@/types/sort';
import { QueryKey } from '@/lib/queryKeys';

// Without this a primed entry is refetched the moment its key is observed, since staleTime is 0.
const ASSET_LIST_STALE_TIME = 30_000;

export type AssetListQueryParams = {
  folderId?: string;
  albumId?: string;
  limit: number;
  offset: number;
  sort?: SortState;
  queryString?: string;
  assetId?: string;
  includeSubFolderAssets?: boolean;
  includeChildren?: boolean;
};

/** One definition of the key, so a prefetch and the hook cannot drift apart. Null means nothing to fetch. */
export const buildAssetListQueryKey = ({
  folderId,
  albumId,
  limit,
  offset,
  sort,
  queryString,
  assetId,
  includeSubFolderAssets = false,
  includeChildren = false,
}: AssetListQueryParams) => {
  if (folderId) {
    return [
      QueryKey.AssetData,
      {
        folderId,
        includeSubFolderAssets,
        includeChildren,
        limit,
        queryString,
        sort,
        // If we provide assetId that means the offset is irrelevant.
        ...(assetId ? { assetId } : { offset }),
      },
    ];
  }

  if (albumId) {
    return [
      QueryKey.AssetData,
      {
        albumId,
        includeChildren,
        limit,
        ...(assetId ? { assetId } : { offset }),
        queryString,
        sort,
      },
    ];
  }

  return null;
};

export const useAssetList = (
  pagination: { limit: number; offset: number },
  folderId?: string,
  albumId?: string,
  sort?: SortState,
  queryString?: string,
  assetId?: string,
  options?: {
    enabled?: boolean;
  },
  includeSubFolderAssets = false,
  includeChildren = false,
) => {
  const folderAssetsQueryFn = useAuthenticatedQueryFn(getFolderAssets);
  const albumAssetsQueryFn = useAuthenticatedQueryFn(getAlbumAssets);

  const queryKey =
    buildAssetListQueryKey({
      folderId,
      albumId,
      limit: pagination.limit,
      offset: pagination.offset,
      sort,
      queryString,
      assetId,
      includeSubFolderAssets,
      includeChildren,
    }) ?? skipToken;

  const queryFn = folderId
    ? () =>
        folderAssetsQueryFn({
          folderId,
          pagination,
          includeSubFolderAssets,
          includeChildren,
          queryString,
          sort,
          assetId,
        })
    : albumId
      ? () => albumAssetsQueryFn({ albumId, pagination, includeChildren, queryString, sort, assetId })
      : skipToken;

  const mergedOptions = {
    ...options,
    enabled: Boolean(queryFn) && options?.enabled,
  };

  const {
    data: assets,
    isLoading,
    isPending,
    isFetched,
    isFetching,
    error,
    refetch,
  } = useQuery<GetAssetsRoot>({
    queryKey: queryKey as ReadonlyArray<unknown>,
    queryFn: queryFn,
    // Only reuse previous data within the same folder/album; a real move must show the skeleton.
    placeholderData: (previousData, previousQuery) => {
      const previousKey = previousQuery?.queryKey?.[1] as { folderId?: string; albumId?: string } | undefined;
      if (previousKey?.folderId !== folderId || previousKey?.albumId !== albumId) {
        return undefined;
      }
      return keepPreviousData(previousData);
    },
    // The global default is `refetchOnMount: false`; override it so a stale entry still refetches.
    refetchOnMount: true,
    staleTime: ASSET_LIST_STALE_TIME,
    ...mergedOptions,
  });

  return { assets, isLoading, isPending, isFetched, isFetching, error, refetch };
};

type FolderAssetsFetchFn = (params: {
  folderId: string;
  pagination?: { offset: number; limit: number };
  includeSubFolderAssets?: boolean;
  includeChildren?: boolean;
  sort?: SortState;
  queryString?: string;
  assetId?: string;
}) => Promise<GetAssetsRoot>;

type AlbumAssetsFetchFn = (params: {
  albumId: string;
  pagination?: { offset: number; limit: number };
  includeChildren?: boolean;
  sort?: SortState;
  queryString?: string;
  assetId?: string;
}) => Promise<GetAssetsRoot>;

/** Starts a folder or album's asset fetch ahead of the route commit and render. */
export const prefetchAssetList = (
  queryClient: QueryClient,
  params: AssetListQueryParams,
  fetchFns: { getFolderAssets: FolderAssetsFetchFn; getAlbumAssets: AlbumAssetsFetchFn },
) => {
  const queryKey = buildAssetListQueryKey(params);

  if (!queryKey) {
    return;
  }

  const { folderId, albumId, limit, offset, sort, queryString, assetId, includeSubFolderAssets, includeChildren } =
    params;
  const pagination = { limit, offset };

  // Fetcher derived from the same params that built the key above, so the two cannot drift.
  const fetchAssets = folderId
    ? () =>
        fetchFns.getFolderAssets({
          folderId,
          pagination,
          includeSubFolderAssets,
          includeChildren,
          queryString,
          sort,
          assetId,
        })
    : albumId
      ? () => fetchFns.getAlbumAssets({ albumId, pagination, includeChildren, queryString, sort, assetId })
      : undefined;

  if (!fetchAssets) {
    return;
  }

  void queryClient.prefetchQuery({ queryKey, queryFn: fetchAssets });
};
